import type {
  ConflictingPartnerScopeItem,
  MaintenanceRulesStatus,
  MaintenanceSlaContractRecord,
  MaintenanceSlaScopeOptions,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import { buildApiUrl } from '~/app/lib/api-config';
import type {
  CreateLandlordPartnerRequestDto,
  CreateLandlordPartnerResponseDto,
  CreateOemPartnerRequestDto,
  CreateOemPartnerResponseDto,
  ListLandlordsResponseDto,
  ListOemsResponseDto,
  UpdateLandlordSlaRequestDto,
  UpdateLandlordSlaResponseDto,
  UpdateOemContractRequestDto,
  UpdateOemContractResponseDto,
} from '@evimo/client/models';
import type { PartnerSearchResultDto } from '@evimo/types/api/partners/search-partners/partner-search-result.dto';
import { PartnerSearchType } from '@evimo/types/enums/partner-search-type';
import {
  partnersApi,
  partnersRequestInit,
} from '~/app/services/partners/partners-api-instances';

export type MaintenanceConflictError = {
  statusCode: number;
  message: string;
  conflicts?: ConflictingPartnerScopeItem[];
  error: 'CONFLICT';
};

export class PartnerMaintenanceConflictError extends Error {
  public readonly statusCode: number;
  public readonly conflicts: ConflictingPartnerScopeItem[];
  public readonly error: 'CONFLICT';

  constructor(payload: MaintenanceConflictError) {
    super(payload.message);
    this.name = 'PartnerMaintenanceConflictError';
    this.statusCode = payload.statusCode;
    this.conflicts = payload.conflicts ?? [];
    this.error = payload.error;
  }
}

export const PartnersService = {
  listLandlords(organizationId: string): Promise<ListLandlordsResponseDto> {
    return partnersApi.partnersControllerListLandlords(
      { organizationId },
      partnersRequestInit,
    );
  },

  createLandlord(
    organizationId: string,
    dto: CreateLandlordPartnerRequestDto,
  ): Promise<CreateLandlordPartnerResponseDto> {
    return partnersApi.partnersControllerCreateLandlord(
      { organizationId, createLandlordPartnerRequestDto: dto },
      partnersRequestInit,
    );
  },

  updateLandlord(
    organizationId: string,
    relationshipId: string,
    dto: UpdateLandlordSlaRequestDto,
  ): Promise<UpdateLandlordSlaResponseDto> {
    return partnersApi.partnersControllerUpdateLandlord(
      { organizationId, relationshipId, updateLandlordSlaRequestDto: dto },
      partnersRequestInit,
    );
  },

  listOems(organizationId: string): Promise<ListOemsResponseDto> {
    return partnersApi.partnersControllerListOems(
      { organizationId },
      partnersRequestInit,
    );
  },

  createOem(
    organizationId: string,
    dto: CreateOemPartnerRequestDto,
  ): Promise<CreateOemPartnerResponseDto> {
    return partnersApi.partnersControllerCreateOem(
      { organizationId, createOemPartnerRequestDto: dto },
      partnersRequestInit,
    );
  },

  updateOem(
    organizationId: string,
    relationshipId: string,
    dto: UpdateOemContractRequestDto,
  ): Promise<UpdateOemContractResponseDto> {
    return partnersApi.partnersControllerUpdateOem(
      { organizationId, relationshipId, updateOemContractRequestDto: dto },
      partnersRequestInit,
    );
  },

  listPartnerMaintenanceScopeOptions(
    organizationId: string,
    excludeRelationshipId?: string,
  ): Promise<MaintenanceSlaScopeOptions> {
    const qs = new URLSearchParams();
    if (excludeRelationshipId) {
      qs.set('excludeRelationshipId', excludeRelationshipId);
    }
    return partnersJson<MaintenanceSlaScopeOptions>(
      organizationId,
      `/maintenance-rules/scope-options${qs.toString() ? `?${qs.toString()}` : ''}`,
    );
  },

  async listPartnerMaintenanceRules(
    organizationId: string,
  ): Promise<MaintenanceSlaContractRecord[]> {
    const body = await partnersJson<{ items: MaintenanceSlaContractRecord[] }>(
      organizationId,
      '/maintenance-rules',
    );
    return body.items;
  },

  replacePartnerMaintenanceRules(
    organizationId: string,
    record: MaintenanceSlaContractRecord,
    rulesStatus: MaintenanceRulesStatus,
  ): Promise<MaintenanceSlaContractRecord> {
    return partnersJson<MaintenanceSlaContractRecord>(
      organizationId,
      `/${record.relationshipId}/maintenance-rules`,
      {
        method: 'PUT',
        body: JSON.stringify({
          status: rulesStatus,
          timezone: record.timezone,
          businessHoursStart: record.businessHoursStart,
          businessHoursEnd: record.businessHoursEnd,
          workingDays: record.workingDays,
          holidayCalendarCode: record.holidayCalendarCode,
          exclusionsText: record.exclusionsText,
          recurrenceWindowDays: record.recurrenceWindowDays,
          restoredAvailableMinutes: record.restoredAvailableMinutes,
          priceCurrency: record.priceCurrency,
          excludeHolidays: record.excludeHolidays,
          interventionMode: record.interventionMode,
          levels: record.levels.map((level) => ({
            ...level,
            code: level.code,
          })),
          scopeRules: record.scopeRules.map((rule) => ({
            dimension: rule.dimension,
            effect: rule.effect,
            value: rule.value,
            interventionMode: rule.interventionMode,
          })),
        }),
      },
    );
  },

  // Kept as raw fetch: the generated PartnersApi does not expose `q` and `type`
  // query params for the search endpoint, so the custom implementation is used.
  //This feature used for auto-complete in the search input
  async searchPartners(
    organizationId: string,
    q: string,
    type: PartnerSearchType,
  ): Promise<PartnerSearchResultDto[]> {
    const params = new URLSearchParams({ q, type });
    const response = await fetch(
      buildApiUrl(
        `/api/v1/organizations/${organizationId}/partners/search?${params.toString()}`,
      ),
      { credentials: 'include' },
    );

    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Request failed (${response.status})`);
    }

    return (await response.json()) as PartnerSearchResultDto[];
  },
};

async function partnersJson<T>(
  organizationId: string,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(
    buildApiUrl(`/api/v1/organizations/${organizationId}/partners/oems${path}`),
    {
      credentials: 'include',
      ...init,
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    },
  );

  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    let body: unknown = null;
    try {
      body = (await response.json()) as { message?: string | string[] };
      if (Array.isArray((body as { message?: string | string[] }).message)) {
        message = (body as { message: string[] }).message.join('; ');
      } else if ((body as { message?: string }).message) {
        message = (body as { message: string }).message;
      }
    } catch {
      message = (await response.text()) || message;
    }

    if (response.status === 409 && body && typeof body === 'object') {
      const payload = body as Partial<MaintenanceConflictError>;
      if (payload.error === 'CONFLICT') {
        throw new PartnerMaintenanceConflictError({
          statusCode: payload.statusCode ?? 409,
          message: payload.message ?? message,
          conflicts: payload.conflicts,
          error: 'CONFLICT',
        });
      }
    }

    throw new Error(message);
  }

  return (await response.json()) as T;
}
